import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Phone, PhoneCall } from "lucide-react";
import { API, GARMENT_TYPES, PRINT_LOCATIONS } from "../constants";
import { SEO } from "../components/SEO";

const RequestCall = () => {
  const navigate = useNavigate();
  const [requestData, setRequestData] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    phone: '',
    preferred_time: '',
    project_details: '',
    agreedToTerms: false,
    marketingConsent: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const savedData = localStorage.getItem('callRequestData');
    if (savedData) {
      try { setRequestData(JSON.parse(savedData)); } catch { /* general form */ }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const callRequestData = {
        ...formData,
        items: requestData ? [{
          product_id: requestData.product_id,
          quantity: requestData.quantity,
          garment_type: requestData.garment_type,
          print_locations: requestData.print_locations,
          colors: requestData.colors,
          embroidery_size_inches: requestData.embroidery_size_inches,
          is_names_only: requestData.is_names_only,
          price: requestData.estimated_price,
          filename: requestData.filename
        }] : []
      };

      await axios.post(`${API}/call-request`, callRequestData);
      toast.success("We'll call you back shortly!");
      localStorage.removeItem('callRequestData');
      navigate('/');
    } catch (error) {
      console.error('Error submitting call request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="request-callback-page">
      <SEO title="Request a Callback" description="Request a callback from the Fit Print Bros team" path="/request-call" />

      <div className="text-center mb-8">
        <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <PhoneCall className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Request a Callback</h1>
        <p className="text-sm text-gray-500 mt-2">Fill out the form and our team will call you back within 24 hours.</p>
      </div>

      {requestData && (
        <Card className="p-5 mb-6 bg-orange-50 border-orange-200">
          <h3 className="text-sm font-semibold text-orange-800 mb-3">Your Configuration</h3>
          <div className="space-y-1.5 text-sm text-gray-700">
            <div><strong>Service:</strong> {requestData.product_name}</div>
            <div><strong>Quantity:</strong> {requestData.quantity}</div>
            {requestData.garment_type && (
              <div><strong>Garment:</strong> {GARMENT_TYPES[requestData.garment_type]?.name}</div>
            )}
            {requestData.print_locations && requestData.print_locations.length > 0 && (
              <div><strong>Print Locations:</strong> {requestData.print_locations.map(loc => PRINT_LOCATIONS[loc]?.name).join(', ')}</div>
            )}
            {requestData.colors && <div><strong>Colors:</strong> {requestData.colors}</div>}
            {requestData.embroidery_size_inches && (
              <div><strong>Embroidery Size:</strong> {requestData.embroidery_size_inches} sq in</div>
            )}
            {requestData.is_names_only && <div><strong>Type:</strong> Names Only</div>}
            <div><strong>Estimated Price:</strong> ${requestData.estimated_price?.toFixed(2) || 'TBD'}</div>
          </div>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label>Full Name *</Label>
          <Input
            value={formData.customer_name}
            onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
            required
            data-testid="customer-name"
          />
        </div>
        
        <div>
          <Label>Email Address *</Label>
          <Input
            type="email"
            value={formData.customer_email}
            onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
            required
            data-testid="customer-email"
          />
        </div>
        
        <div>
          <Label>Phone Number *</Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            required
            placeholder="(555) 123-4567"
            data-testid="customer-phone"
          />
        </div>
        
        <div>
          <Label>Preferred Call Time</Label>
          <select
            value={formData.preferred_time}
            onChange={(e) => setFormData({...formData, preferred_time: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
            data-testid="preferred-time"
          >
            <option value="">Select preferred time</option>
            <option value="9:00 AM - 10:00 AM PST">9:00 AM - 10:00 AM PST</option>
            <option value="10:00 AM - 11:00 AM PST">10:00 AM - 11:00 AM PST</option>
            <option value="11:00 AM - 12:00 PM PST">11:00 AM - 12:00 PM PST</option>
            <option value="12:00 PM - 1:00 PM PST">12:00 PM - 1:00 PM PST</option>
            <option value="1:00 PM - 2:00 PM PST">1:00 PM - 2:00 PM PST</option>
            <option value="2:00 PM - 3:00 PM PST">2:00 PM - 3:00 PM PST</option>
            <option value="3:00 PM - 4:00 PM PST">3:00 PM - 4:00 PM PST</option>
            <option value="4:00 PM - 5:00 PM PST">4:00 PM - 5:00 PM PST</option>
            <option value="After Hours">After Hours (Best effort within 24hr)</option>
          </select>
          <p className="text-[11px] text-gray-500 mt-1">
            Mon–Fri, 9 AM – 5 PM PST. After-hours requests returned within 24 business hours.
          </p>
        </div>
        
        <div>
          <Label>What can we help you with?</Label>
          <Textarea
            value={formData.project_details}
            onChange={(e) => setFormData({...formData, project_details: e.target.value})}
            placeholder="Tell us about your project, timeline, quantity, special requirements..."
            rows={4}
            data-testid="project-details"
          />
        </div>

        {/* Legal Compliance */}
        <div className="space-y-3 border-t pt-4">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="terms-agreement"
              checked={formData.agreedToTerms}
              onChange={(e) => setFormData({...formData, agreedToTerms: e.target.checked})}
              className="mt-1 mr-3"
              required
            />
            <label htmlFor="terms-agreement" className="text-sm text-gray-700">
              I agree to the{' '}
              <Link to="/terms" className="text-orange-600 hover:underline" target="_blank">Terms & Conditions</Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-orange-600 hover:underline" target="_blank">Privacy Policy</Link>
              <span className="text-red-500 ml-1">*</span>
            </label>
          </div>
          
          <div className="flex items-start">
            <input
              type="checkbox"
              id="marketing-consent"
              checked={formData.marketingConsent}
              onChange={(e) => setFormData({...formData, marketingConsent: e.target.checked})}
              className="mt-1 mr-3"
            />
            <label htmlFor="marketing-consent" className="text-sm text-gray-700">
              I'd like to receive special offers and updates from Fit Print Bros (optional)
            </label>
          </div>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
          disabled={submitting || !formData.agreedToTerms}
          data-testid="submit-call-request"
        >
          <Phone className="w-4 h-4 mr-2" />
          {submitting ? 'Submitting...' : 'Request Callback'}
        </Button>
      </form>
    </div>
  );
};

export { RequestCall };
export default RequestCall;
